import { Fragment } from "react";

import { COMMODITY_NAME_COTTON } from "@datanac/datanac-api-toolkit/dist/datanac-rma-api-helper";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import { styled, useColorScheme } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import { getCellColor, getHeaderColor } from "./StressTestVisualization/getCellColor";

export default function InsuranceOptimizeDataGrid({
  title,
  commodity_name,
  insurance_plan_uuid,
  displayFieldName,

  xAxisFieldName,
  xAxisDisplayName,

  yAxisFieldName,
  yAxisDisplayName,

  doMarkNegativeValues,

  stressTestResults,

  maxLossValue,
  formatYAxisAsDollars = true,
  isShowAxisLabels = true,
}) {
  const { mode } = useColorScheme();
  const getXAxis = () => {
    if (stressTestResults) {
      const xAxisElements = stressTestResults[0]?.map((row) => {
        return row[xAxisFieldName || "yield"];
      });

      return xAxisElements;
    }
  };

  // TODO maybe replace line 48-68 it with utility/formatters
  const _priceFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    currencySign: "accounting",
    maximumFractionDigits: 0,
  });
  const _priceFormatter2 = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    currencySign: "accounting",
    maximumFractionDigits: 2,
  });

  const formatPrice = (value) => {
    return value || value === 0 ? _priceFormatter.format(value)?.replace("$", "$ ") : "";
  };

  const formatPrice2 = (value) => {
    return value || value === 0 ? _priceFormatter2.format(value)?.replace("$", "$ ") : "";
  };

  const getDisplayValue = (cell, insurance_plan_uuid, displayFieldName) => {
    let displayValue;
    if (insurance_plan_uuid) {
      const insurancePlan = cell?.indemnityResults?.find((r) => r.insurance_plan_uuid === insurance_plan_uuid);
      if (insurancePlan) {
        displayValue = insurancePlan[displayFieldName];
      }
    } else {
      displayValue = cell[displayFieldName];
    }
    return displayValue;
  };

  const CustomWidthTooltip = styled(({ className, ...props }) => (
    <Tooltip
      {...props}
      classes={{ popper: className }}
      enterDelay={1000}
      enterTouchDelay={1000}
      enterNextDelay={1000}
    />
  ))({
    [`& .${tooltipClasses.tooltip}`]: {
      maxWidth: "none",
    },
  });

  const filterCotton = (cell, commodity_name) => {
    return commodity_name !== COMMODITY_NAME_COTTON
      ? {
          ...cell,
          yield_seedcotton: undefined,
          county_yield_seedcotton: undefined,
        }
      : cell;
  };

  return (
    <>
      <TableContainer sx={{ boxSizing: "border-box" }} className="insuranceOptimizeDataGrid">
        {title && (
          <Box sx={{ p: 1 }}>
            <Typography>{title}</Typography>
          </Box>
        )}
        <Table
          padding="none"
          spacing={0}
          size="small"
          sx={{
            [`& .${tableCellClasses.root}`]: {
              borderBottom: "none",
              fontSize: {
                xs: "0.75rem",
                sm: "0.875rem",
              },
            },
          }}
        >
          <TableBody>
            <TableRow>
              <TableCell
                colSpan={1}
                sx={{
                  textAlign: "center",
                  padding: "0 !important",
                  paddingTop: "4pt !important",
                  paddingBottom: "4pt !important",
                  paddingRight: "4pt !important",
                  paddingLeft: "4pt !important",
                  backgroundColor: "background.paper",
                  borderRadius: "8px 0 0 0",
                }}
              >
                {yAxisDisplayName || "Price"}
              </TableCell>
              <TableCell
                colSpan="10"
                sx={{
                  textAlign: "center",
                  backgroundColor: "background.paper",
                  borderRadius: "0 8px 0 0",
                }}
              >
                {xAxisDisplayName || "Yield: Low → High"}
              </TableCell>
            </TableRow>
            <TableRow
              sx={{
                borderBottom: "1px solid",
                borderColor: "colors.borderLight",
              }}
            >
              <TableCell
                sx={{
                  padding: "0 !important",
                  backgroundColor: "background.paper",
                }}
              >
                &nbsp;
              </TableCell>
              {(() => {
                const xAxisItems = getXAxis();
                const totalXItems = xAxisItems?.length || 0;
                return xAxisItems?.map((x, index) => {
                  const isFirst = index === 0;
                  const isLast = index === totalXItems - 1;

                  // Get the cell data for this column to show yields in tooltip
                  const cellForColumn = filterCotton(stressTestResults?.[0]?.[index], commodity_name);

                  return (
                    <CustomWidthTooltip
                      key={`x-axis-tooltip-${index}`}
                      arrow
                      title={
                        cellForColumn ? (
                          <>
                            <Typography>
                              <strong>Yields for this column:</strong>
                              <br />
                              &nbsp;&nbsp;Yield: {cellForColumn["yield"]}
                              {Boolean(cellForColumn["yield_seedcotton"]) && (
                                <>
                                  <br />
                                  &nbsp;&nbsp;Yield (Seed Cotton): {cellForColumn["yield_seedcotton"]}
                                </>
                              )}
                              <br />
                              <br />
                              <br />
                              <strong>County Yields:</strong>
                              <br />
                              &nbsp;&nbsp;County Yield: {cellForColumn["county_yield"]}
                              {Boolean(cellForColumn["county_yield_seedcotton"]) && (
                                <>
                                  <br />
                                  &nbsp;&nbsp;County Yield (Seed Cotton): {cellForColumn["county_yield_seedcotton"]}
                                </>
                              )}
                              <br />
                            </Typography>
                          </>
                        ) : null
                      }
                    >
                      <TableCell
                        key={index}
                        sx={{
                          textAlign: "right",
                          padding: "0 !important",
                          paddingTop: "4pt !important",
                          paddingBottom: "4pt !important",
                          paddingRight: "4pt !important",
                          borderBottom: "1px solid var(--mui-palette-grey-) !important",
                          backgroundColor: getHeaderColor(index, totalXItems, mode !== "light"),
                          fontSize: {
                            xs: "0.75rem",
                            sm: "0.75rem",
                          },
                        }}
                      >
                        {isShowAxisLabels ? Math.round(x) : isFirst ? "Low" : isLast ? "High" : ""}
                      </TableCell>
                    </CustomWidthTooltip>
                  );
                });
              })()}
            </TableRow>

            {stressTestResults &&
              stressTestResults?.length > 0 &&
              stressTestResults
                ?.filter((row, index) =>
                  stressTestResults[0][0].price !==
                  stressTestResults[stressTestResults.length - 1][stressTestResults.length - 1].price
                    ? true
                    : index === 0
                      ? true
                      : false
                )
                .map((row, rowIndex) => {
                  const filteredResults = stressTestResults?.filter((row, index) =>
                    stressTestResults[0][0].price !==
                    stressTestResults[stressTestResults.length - 1][stressTestResults.length - 1].price
                      ? true
                      : index === 0
                        ? true
                        : false
                  );
                  const totalYItems = filteredResults?.length || 0;
                  const isLastRow = rowIndex === totalYItems - 1;

                  return (
                    <TableRow key={rowIndex}>
                      <TableCell
                        sx={{
                          borderRight: "1px solid",
                          borderColor: "colors.borderLight",
                          p: 0,
                          textAlign: "center",
                          paddingTop: "0 !important",
                          paddingBottom: "0 !important",
                          paddingLeft: "8px !important",
                          paddingRight: "8px !important",
                          maxWidth: { xs: "6rem", md: "5rem", lg: "4rem" },
                          backgroundColor: getHeaderColor(rowIndex, totalYItems, mode !== "light"),

                          fontSize: "0.75rem",
                          borderRadius: isLastRow ? "0 0 0 8px" : "0",
                        }}
                      >
                        {isShowAxisLabels
                          ? formatYAxisAsDollars && yAxisFieldName === "price"
                            ? formatPrice2(row[0]?.[yAxisFieldName])
                            : row[0]?.[yAxisFieldName]
                          : rowIndex === 0
                            ? "Low"
                            : rowIndex === totalYItems - 1
                              ? "High"
                              : ""}
                      </TableCell>
                      {row.map((cell, index) => {
                        const isLastCell = index === row.length - 1;
                        cell = filterCotton(cell, commodity_name);
                        return (
                          <CustomWidthTooltip
                            key={"tooltip" + index}
                            sx={{ maxWidth: 1000 }}
                            arrow
                            title={
                              <>
                                <Typography>
                                  <strong>Yields:</strong>
                                  <br />
                                  &nbsp;&nbsp;Yield: {cell["yield"]}
                                  {Boolean(cell["yield_seedcotton"]) && (
                                    <>
                                      <br />
                                      &nbsp;&nbsp;Yield (Seed Cotton): {cell["yield_seedcotton"]}
                                    </>
                                  )}
                                  <br />
                                  <br />
                                  <br />
                                  <strong>County Yields:</strong>
                                  <br />
                                  &nbsp;&nbsp;County Yield: {cell["county_yield"]}
                                  {Boolean(cell["county_yield_seedcotton"]) && (
                                    <>
                                      <br />
                                      &nbsp;&nbsp;County Yield (Seed Cotton): {cell["county_yield_seedcotton"]}
                                    </>
                                  )}
                                  <br />
                                  <br />
                                  <br />
                                  <strong>Prices:</strong>
                                  <br />
                                  &nbsp;&nbsp;Price: {formatPrice2(cell["price"])}
                                  <br />
                                  {Boolean(cell["price_cottonseed"]) && (
                                    <>
                                      &nbsp;&nbsp;Cottonseed Price: {cell["price_cottonseed"]?.toFixed(4)}
                                      <br />
                                    </>
                                  )}
                                  <br />
                                  <strong>Net Profit without Insurance:</strong>{" "}
                                  <u>{formatPrice(cell["net_profit_simple"])}</u>
                                  <br />
                                </Typography>
                                <Divider />
                                <Typography>
                                  <strong>Insurance Coverage:</strong>
                                  <br />
                                  {cell.indemnityResults &&
                                    cell.indemnityResults.length > 0 &&
                                    cell.indemnityResults.map((r, index) => {
                                      let current_insurance_plan_name = r.insurance_plan_name;
                                      if (
                                        Array.isArray(r.insurance_option_codes) &&
                                        r.insurance_option_codes.includes("SE")
                                      ) {
                                        current_insurance_plan_name += " (SE)";
                                      }
                                      return (
                                        <Fragment key={index}>
                                          &nbsp;&nbsp;{current_insurance_plan_name}:{" "}
                                          {formatPrice(r.indemnity_total_per_acre)}
                                          <br />
                                        </Fragment>
                                      );
                                    })}
                                  <br />
                                  <strong>Total Indemnity:</strong> {formatPrice(cell["indemnity_total_per_acre"])}
                                  <br />
                                </Typography>
                                <Divider />
                                <Typography>
                                  <strong>Final Result - Insured Net Profit:</strong>{" "}
                                  <u>{formatPrice(cell["net_profit_insured"])}</u>
                                  <br />
                                </Typography>
                              </>
                            }
                          >
                            <TableCell
                              key={"cell" + index}
                              sx={{
                                paddingLeft: "4pt !important",
                                paddingRight: "8pt !important",
                                paddingTop: "8pt !important",
                                paddingBottom: "8pt !important",
                                textAlign: "right",
                                backgroundColor: getCellColor(
                                  getDisplayValue(cell, insurance_plan_uuid, displayFieldName),
                                  maxLossValue,
                                  doMarkNegativeValues,
                                  false,
                                  false,
                                  mode !== "light"
                                ),
                                borderRadius: isLastRow && isLastCell ? "0 0 8px 0" : "0",
                              }}
                            >
                              {formatPrice(getDisplayValue(cell, insurance_plan_uuid, displayFieldName)) || " "}
                              <br />
                            </TableCell>
                          </CustomWidthTooltip>
                        );
                      })}
                    </TableRow>
                  );
                })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
